import { useMemo, Fragment } from 'react';
import parse, {
  attributesToProps,
  domToReact,
  type HTMLReactParserOptions,
  type DOMNode,
  type Element,
} from 'html-react-parser';
import useStyles from './styles';
import Typography from '@mui/material/Typography';

/**
 * Type for HTML element attributes (allows strings and booleans)
 */
type ElementAttributes = Record<string, string | boolean>;

/**
 * Prefer `type === 'tag'` over `instanceof Element`.
 * html-react-parser v6 / domhandler v6 can break `instanceof` across dual package
 * copies, and re-entering replace via domToReact on reconstructed nodes relied on
 * that failure mode before.
 */
const isElement = (node: DOMNode): node is Element =>
  Boolean(node && node.type === 'tag' && 'name' in node && 'attribs' in node);

/**
 * Required attributes for YouTube/Vimeo iframes to ensure proper embedding
 * and security policies.
 */
const REQUIRED_VIDEO_ATTRIBUTES = {
  allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
  allowFullScreen: true,
} as const;

const LEGACY_IFRAME_ATTRS = new Set([
  'frameborder',
  'allowfullscreen',
  'webkitallowfullscreen',
  'mozallowfullscreen',
]);

/**
 * Strips HTML/DOM attribute names that React does not accept on iframes and maps
 * allowfullscreen → allowFullScreen when needed.
 */
const normalizeIframeAttribs = (attribs: ElementAttributes): ElementAttributes => {
  const next: ElementAttributes = { ...attribs };
  const hadLegacyFullscreen =
    next.allowfullscreen === true ||
    next.allowfullscreen === '' ||
    String(next.allowfullscreen ?? '').toLowerCase() === 'allowfullscreen';
  const explicitFullScreen = next.allowFullScreen;
  for (const key of LEGACY_IFRAME_ATTRS) {
    delete next[key];
  }
  if (hadLegacyFullscreen && explicitFullScreen === undefined) {
    next.allowFullScreen = true;
  }
  return next;
};

/**
 * Merges required YouTube/Vimeo iframe attributes with existing attributes.
 * Ensures security and functionality attributes are always present.
 *
 * @param existingAttributes - Existing attributes from the HTML element
 * @returns Merged attributes with required attributes taking precedence
 */
const mergeYouTubeAttributes = (
  existingAttributes: ElementAttributes,
): ElementAttributes => {
  return {
    ...normalizeIframeAttribs(existingAttributes),
    ...REQUIRED_VIDEO_ATTRIBUTES,
  };
};

/**
 * Duplicates attributes for html-react-parser compatibility.
 * Preserves original attributes in x-attribsNamespace and x-attribsPrefix
 * to maintain compatibility with the parser.
 *
 * @param node - DOM node to process
 * @returns Processed DOM node with duplicated attributes
 */
const dupAttribs = (node: DOMNode): DOMNode => {
  if (!isElement(node)) return node;

  // Skip script tags for security
  if (node.name === 'script') return node;

  // Preserve attributes for parser compatibility
  if (
    node.attribs &&
    !(node as Element & { 'x-attribsNamespace'?: ElementAttributes })[
      'x-attribsNamespace'
    ]
  ) {
    (node as Element & { 'x-attribsNamespace': ElementAttributes })[
      'x-attribsNamespace'
    ] = node.attribs;
  }

  if (
    node.attribs &&
    !(node as Element & { 'x-attribsPrefix'?: ElementAttributes })['x-attribsPrefix']
  ) {
    (node as Element & { 'x-attribsPrefix': ElementAttributes })['x-attribsPrefix'] =
      node.attribs;
  }

  // Process children recursively
  if (node.children) node.children.forEach(dupAttribs);

  return node;
};

/**
 * Checks if a URL is external (not from the same origin).
 * Handles SSR by treating relative URLs as internal.
 *
 * @param href - URL to check
 * @returns True if URL is external
 */
const isExternalUrl = (href: string): boolean => {
  // Treat relative URLs as internal
  if (href.startsWith('/') || href.startsWith('#')) {
    return false;
  }

  // Check for protocol (http://, https://, etc.)
  if (!href.includes('://')) {
    return false;
  }

  try {
    // In browser environment, compare origins
    if (typeof window !== 'undefined') {
      const url = new URL(href, window.location.origin);
      return url.origin !== window.location.origin;
    }
    // In SSR, any absolute URL is considered external
    return true;
  } catch {
    // Invalid URL, treat as internal
    return false;
  }
};

/**
 * PostBody Component
 *
 * Parses and renders HTML content from post bodies, converting HTML elements
 * into styled React components using Material-UI.
 *
 * Features:
 * - Parses paragraphs, links, images, videos, audio embeds, blockquotes
 * - Supports social media embeds (Instagram, Twitter)
 * - Handles YouTube/Vimeo video embeds with responsive containers
 * - Supports audio embeds (Spotify, SoundCloud, Tidal)
 * - Applies consistent styling using Material-UI components
 * - Provides accessibility improvements (alt text, external link attributes)
 *
 * Security Note:
 * HTML content should be sanitized before being passed to this component
 * to prevent XSS attacks. Consider using DOMPurify or similar sanitization
 * library at the API/data fetching layer.
 *
 * @param props - Component props
 * @param props.content - HTML string to parse and render
 */
const PostBody = (props: PostBodyPropTypes) => {
  const { classes } = useStyles();
  const { content } = props;

  /**
   * Creates HTML parser options configuration.
   * Intercepts HTML element parsing and replaces them with styled React components.
   * Uses Material-UI components for consistent styling.
   *
   * Security Note: HTML content should be sanitized before parsing to prevent XSS.
   * Consider implementing sanitization at the data fetching layer.
   */
  const createHtmlParserOptions = useMemo((): HTMLReactParserOptions => {
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        // Type guard - only process Element nodes
        if (!isElement(domNode)) return;

        // Handle malformed nodes (patches CAB-168)
        if (!domNode.name) return null;

        const processedNode = dupAttribs(domNode) as Element;
        if (!processedNode || !processedNode.name) return null;

        const nodeName = processedNode.name;
        const nodeAttribs = processedNode.attribs || {};

        // Paragraph elements — use div so block embeds (e.g. wrapped iframes) from
        // CMS HTML are valid; WordPress often outputs <p><iframe/></p>.
        if (nodeName === 'p') {
          return (
            <Typography
              component="div"
              variant="body1"
              className={classes.styledText}
              data-postbody-p
            >
              {domToReact(processedNode.children as DOMNode[], options)}
            </Typography>
          );
        }

        // Span elements
        if (nodeName === 'span') {
          if (nodeAttribs.style) {
            // Spans with inline styles are rendered without wrapper
            return (
              <Fragment>
                {domToReact(processedNode.children as DOMNode[], options)}
              </Fragment>
            );
          }
          return <span>{domToReact(processedNode.children as DOMNode[], options)}</span>;
        }

        // Anchor elements
        if (nodeName === 'a') {
          const href = nodeAttribs.href || '';
          const linkProps: Record<string, unknown> = {
            href,
            className: classes.styledLink,
            ...nodeAttribs,
          };

          // Add security attributes for external links
          if (href && isExternalUrl(href)) {
            linkProps.rel = 'noopener noreferrer';
            linkProps.target = '_blank';
          }

          return (
            <a {...linkProps}>
              {domToReact(processedNode.children as DOMNode[], options)}
            </a>
          );
        }

        // Bold/Strong elements
        if (nodeName === 'b' || nodeName === 'strong') {
          return (
            <b className={classes.styledBold}>
              {domToReact(processedNode.children as DOMNode[], options)}
            </b>
          );
        }

        // Blockquote elements
        if (nodeName === 'blockquote') {
          const blockquoteClass = nodeAttribs.class;

          // Instagram embed
          if (blockquoteClass === 'instagram-media') {
            return (
              <div className={classes.socialMediaEmbedContainer}>
                <blockquote className="instagram-media" aria-label="Instagram embed">
                  {domToReact(processedNode.children as DOMNode[], options)}
                </blockquote>
              </div>
            );
          }

          // Twitter embed
          if (blockquoteClass === 'twitter-tweet') {
            return (
              <div className={classes.socialMediaEmbedContainer}>
                <blockquote className="twitter-tweet" aria-label="Twitter embed">
                  {domToReact(processedNode.children as DOMNode[], options)}
                </blockquote>
              </div>
            );
          }

          // Regular blockquote
          return (
            <blockquote className={classes.styledBlockQuote}>
              {domToReact(processedNode.children as DOMNode[], options)}
            </blockquote>
          );
        }

        // Image elements
        if (nodeName === 'img') {
          const src = nodeAttribs.src || '';
          const alt = nodeAttribs.alt || '';
          const srcSet = nodeAttribs.srcset || null;

          // Provide fallback alt text if missing
          const imageAlt = alt || 'Post image';

          if (!src) {
            // Skip images without src
            return null;
          }

          return (
            <img
              src={src}
              srcSet={srcSet || undefined}
              alt={imageAlt}
              className={classes.styledImage}
              loading="lazy"
            />
          );
        }

        // Iframe elements (videos, audio embeds)
        if (nodeName === 'iframe') {
          const src = nodeAttribs.src || '';

          if (!src) {
            return null;
          }

          // Remove deprecated frameborder attribute (normalizeIframeAttribs strips the rest)
          const { frameborder, ...rest } = nodeAttribs;
          const cleanAttribs = normalizeIframeAttribs(rest);

          // YouTube/Vimeo video embeds
          if (src.includes('youtu') || src.includes('vimeo')) {
            // Reference: https://github.com/toddmotto/fluidvids-react/blob/master/react.fluidvids.js
            const width = cleanAttribs.width;
            const height = cleanAttribs.height;

            if (width && height) {
              const widthNum = parseInt(String(width), 10);
              const heightNum = parseInt(String(height), 10);

              if (widthNum > 0 && heightNum > 0) {
                const ratio = (heightNum / widthNum) * 100;

                return (
                  <div
                    className={classes.responsiveEmbedContainer}
                    style={{ paddingTop: `${ratio}%` }}
                  >
                    <iframe
                      {...mergeYouTubeAttributes(cleanAttribs)}
                      className={classes.styledVideoEmbed}
                      title={`Embedded video: ${src}`}
                      aria-label={`Embedded video from ${src.includes('youtu') ? 'YouTube' : 'Vimeo'}`}
                    >
                      {domToReact(processedNode.children as DOMNode[], options)}
                    </iframe>
                  </div>
                );
              }
            }

            // Fallback for iframes without valid dimensions
            return (
              <iframe
                {...mergeYouTubeAttributes(cleanAttribs)}
                className={classes.styledVideoEmbed}
                title={`Embedded video: ${src}`}
              />
            );
          }

          // Audio embeds (Spotify, SoundCloud, Tidal)
          if (
            src.includes('soundcloud') ||
            src.includes('spotify') ||
            src.includes('tidal')
          ) {
            return (
              <iframe
                {...attributesToProps(cleanAttribs as Record<string, string>)}
                className={classes.styledAudioEmbed}
                allow="encrypted-media"
                title={`Embedded audio: ${src}`}
                aria-label={`Embedded audio from ${src.includes('spotify') ? 'Spotify' : src.includes('soundcloud') ? 'SoundCloud' : 'Tidal'}`}
              />
            );
          }

          // Generic iframe — render directly (do not re-enter replace via domToReact)
          return (
            <iframe
              {...attributesToProps(cleanAttribs as Record<string, string>)}
              title={
                typeof cleanAttribs.title === 'string'
                  ? cleanAttribs.title
                  : `Embedded content: ${src}`
              }
            />
          );
        }

        // Div elements
        if (nodeName === 'div') {
          const {
            style: _style,
            class: _class,
            className: _className,
            ...rest
          } = nodeAttribs;
          return (
            <div
              {...attributesToProps(rest as Record<string, string>)}
              className={classes.styledDiv}
            >
              {domToReact(processedNode.children as DOMNode[], options)}
            </div>
          );
        }

        // Default: recursively process children
        return domToReact(processedNode.children as DOMNode[], options);
      },
    };

    return options;
  }, [classes]);

  // Parse and render HTML content with error handling
  const parsedContent = useMemo(() => {
    if (!content) return null;

    try {
      return parse(content, createHtmlParserOptions);
    } catch (error) {
      // Log error but don't crash - return fallback
      console.error('Error parsing post body HTML:', error);
      return <div className={classes.root}>Error rendering post content</div>;
    }
  }, [content, createHtmlParserOptions, classes.root]);

  return (
    <div className={classes.root} data-component="PostBody">
      {parsedContent}
    </div>
  );
};

export interface PostBodyPropTypes {
  /** HTML string content to parse and render */
  content: string;
}

export default PostBody;
